import { useState } from "react";
import { Button } from "@openup/shared/primitives/ui/button";
import { Textarea } from "@openup/shared/primitives/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@openup/shared/primitives/ui/alert-dialog";
import FormField from "@src/components/spaces/FormField";

export interface RescheduleConfirmSession {
  id: string;
  /** e.g. the session name or "Session 2". */
  title: string;
  /** Formatted current schedule, e.g. "1 Jul 2036, 10:00 (Europe/Amsterdam)". */
  previousTime: string;
  /** Formatted schedule being saved. */
  newTime: string;
  /** Reason typed on the session card; editable here before confirming. */
  reason: string;
}

interface RescheduleConfirmDialogProps {
  open: boolean;
  sessions: RescheduleConfirmSession[];
  pending: boolean;
  /** Confirmed reasons keyed by session id (trimmed card value if untouched). */
  onConfirm: (reasons: Record<string, string>) => void;
  onClose: () => void;
}

/**
 * Confirmation shown before a save that reschedules sessions (OI-24717). The
 * reason field on the session card is easy to miss, so the dialog surfaces it
 * one last time: blank → a nudge to add one (still optional), filled → a
 * prompt to confirm it, since it lands verbatim in the attendees'
 * reschedule email (`rescheduleAdditionalInformation`).
 */
const RescheduleConfirmDialog = ({
  open,
  sessions,
  pending,
  onConfirm,
  onClose,
}: RescheduleConfirmDialogProps) => {
  // Edits made inside the dialog; null = untouched, fall back to card values.
  const [edited, setEdited] = useState<Record<string, string> | null>(null);
  const reasons =
    edited ??
    Object.fromEntries(sessions.map((session) => [session.id, session.reason]));

  const close = () => {
    setEdited(null);
    onClose();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next: boolean) => !next && !pending && close()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {sessions.length === 1
              ? "Reschedule this session?"
              : `Reschedule ${sessions.length} sessions?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            Registered attendees will be emailed about the new schedule when you
            save.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex max-h-80 flex-col gap-4 overflow-y-auto">
          {sessions.map((session) => {
            const reason = reasons[session.id] ?? "";
            return (
              <div key={session.id} className="rounded-lg bg-orange-100 p-4">
                <p className="text-sm font-medium text-indigo-800">
                  {session.title}
                </p>
                <p className="mb-2 text-sm text-gray-700">
                  {session.previousTime} → {session.newTime}
                </p>
                <FormField label="Reason for rescheduling">
                  {({ id }) => (
                    <Textarea
                      id={id}
                      rows={2}
                      maxLength={2000}
                      value={reason}
                      placeholder="Optional — shown to attendees in the reschedule email"
                      onChange={(e) =>
                        setEdited({ ...reasons, [session.id]: e.target.value })
                      }
                    />
                  )}
                </FormField>
                <p className="mt-1 text-xs text-indigo-800">
                  {reason.trim()
                    ? "This reason is included in the email — please check it before saving."
                    : "No reason added — you can add one to tell attendees why the time changed (optional)."}
                </p>
              </div>
            );
          })}
        </div>

        {/* Layout classes repeated here so Tailwind generates them — the shared
            primitive's own classes aren't scanned by this app's build. */}
        <AlertDialogFooter className="flex-row justify-end">
          <Button variant="ghost" onClick={close} disabled={pending}>
            Go back
          </Button>
          <Button
            disabled={pending}
            onClick={() => {
              // The parent writes these back into the form, so props carry the
              // confirmed values from here on — drop the local edits.
              setEdited(null);
              onConfirm(
                Object.fromEntries(
                  sessions.map((session) => [
                    session.id,
                    (reasons[session.id] ?? "").trim(),
                  ]),
                ),
              );
            }}
          >
            {pending ? "Saving…" : "Confirm and save"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RescheduleConfirmDialog;
